import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageComponent } from '../../../../../services/website.service';

interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  description?: string;
}

@Component({
  selector: 'app-map-component',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './map-component.component.html',
  styleUrls: ['./map-component.component.scss']
})
export class MapComponentComponent {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Output() contentChange = new EventEmitter<void>();
  @Output() styleChange = new EventEmitter<void>();

  // Getters
  getHeading(): string { return this.component.data['heading'] || ''; }
  getSubtitle(): string { return this.component.data['subtitle'] || ''; }
  getLat(): number { return this.component.data['lat'] || 52.3676; }
  getLng(): number { return this.component.data['lng'] || 4.9041; }
  getZoom(): number { return this.component.data['zoom'] || 13; }
  getMapHeight(): number { return this.component.data['mapHeight'] || 400; }
  getMapHeightUnit(): string { return this.component.data['mapHeightUnit'] || 'px'; }
  getBorderRadius(): number { return this.component.data['borderRadius'] ?? 8; }
  getShowZoomControl(): boolean { return this.component.data['showZoomControl'] !== false; }
  getScrollWheelZoom(): boolean { return this.component.data['scrollWheelZoom'] || false; }
  getMarkers(): MapMarker[] { return this.component.data['markers'] || []; }

  // Styling getters (same pattern as other components)
  getBackgroundType(): string { return this.component.data['backgroundType'] || 'solid'; }
  getBackgroundColor(): string { return this.component.data['backgroundColor'] || '#ffffff'; }
  getGradientFrom(): string { return this.component.data['gradientFrom'] || '#ffffff'; }
  getGradientTo(): string { return this.component.data['gradientTo'] || '#ffffff'; }
  getBackgroundImage(): string { return this.component.data['backgroundImage'] || ''; }
  getHeadingColor(): string { return this.component.data['headingColor'] || '#111827'; }
  getTextColor(): string { return this.component.data['textColor'] || '#374151'; }
  getTitlePosition(): string { return this.component.data['titlePosition'] || 'left'; }
  getParallax(): boolean { return this.component.data['parallax'] || false; }
  getSectionHeight(): string { return this.component.data['sectionHeight'] || ''; }
  getHeightUnit(): string { return this.component.data['heightUnit'] || 'px'; }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      styles['min-height'] = `${sectionHeight}${this.getHeightUnit()}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  }
}
